import React, { useEffect, useState, useMemo } from 'react'

import { useToasts } from 'react-toast-notifications'
import { useDispatch, useSelector } from 'react-redux'
import Joi from 'joi'

import { H6 } from '../../../../style/Typography.style'

import EmailParameterForm from './SendEmailReminders/components/EmailParameterForm'
import GroupedCheckbox from '../../../../components/GroupedCheckbox'
import MainModal from '../../../../components/dashboard/modal/MainModal'

import { setModal } from '../../../../store/actions/listsActions'

import {
  setUpdatedStage,
  setBulkItems,
} from '../../../../store/actions/recruitersActions'

import useMutation from '../../../../hooks/useMutation'

import { referralEmail } from '../../../../constant/constant'

const BulkSendEmailReminders = () => {
  const { addToast } = useToasts()
  const dispatch = useDispatch()

  const { selectedStage } = useSelector((state) => state.requisitions)
  const bulkItems = useSelector((state) => state.requisitions.bulkItems)

  const validationSchema = useMemo(() => {
    return Joi.object({
      remote_fitness_referral_email: Joi.string()
        .label('Referral Email')
        .email({ tlds: { allow: false } })
        .optional(),
    })
  }, [])

  const [emailReminders, setEmailReminders] = useState([
    {
      id: 0,
      title: 'Interview Scheduling Reminder',
      template: 'Scheduled Reminder',
      checked: false,
    },
    {
      id: 1,
      title: 'Remote Fitness Assessment Reminder',
      template: 'Remote Fitness Assessment Reminder',
      checked: false,
    },
    {
      id: 3,
      title: 'Coaching Interview Reminder',
      template: 'Coaching Interview Reminder',
      checked: false,
    },
  ])
  const [emailParameters, setEmailParameters] = useState({})
  const [fieldErrors, setFieldErrors] = useState({})

  const { data, loading, handleRequest, success, error } = useMutation()

  const getTotalSelectedItems = () =>
    Object.values(bulkItems).reduce(
      (total, item) => total + item.selectedItems,
      0
    )

  const handleClose = () => {
    dispatch(setModal(''))
  }

  const handleGroupedCheckboxChange = (checkboxId, checked) => {
    setEmailReminders((prevState) =>
      prevState.map((item) =>
        item.id === checkboxId ? { ...item, checked } : item
      )
    )
  }

  const handleEmailParameterChange = (name, value) => {
    setEmailParameters((prev) => ({ ...prev, [name]: value }))
  }

  const handleSubmit = () => {
    const result = validationSchema.validate(emailParameters, {
      abortEarly: false,
    })

    if (emailReminders.every((reminder) => !reminder.checked)) {
      addToast('Please select at least one email reminder to send.', {
        appearance: 'error',
        autoDismiss: true,
      })
      return
    }

    if (result.error) {
      const validationErrors = result.error.details.reduce((acc, curr) => {
        acc[curr.path[0]] = curr.message.replace(/"/g, '')
        return acc
      }, {})

      addToast('Please fix the validation errors to continue.', {
        appearance: 'error',
        autoDismiss: true,
      })

      setFieldErrors(validationErrors)

      return
    }

    const cleanedBulkItems = Object.entries(bulkItems)
      .filter(([_, value]) => value.selectedItems > 0)
      .reduce((acc, [key, value]) => {
        const { stage, ...rest } = value
        acc[key] = rest
        return acc
      }, {})

    const bulkPayload = {
      bulk_items: cleanedBulkItems,
      email_reminders: emailReminders
        .filter((cb) => Boolean(cb.checked))
        .map((cb) => cb.template),
      email_parameters: JSON.stringify(emailParameters),
    }

    handleRequest(`/interviews/bulk-send-email-reminders`, bulkPayload, 'POST')
  }

  useEffect(() => {
    if (data) {
      addToast(data.message, {
        appearance: 'success',
        autoDismiss: true,
      })

      dispatch(setUpdatedStage(selectedStage))

      Object.entries(data.resp).forEach(([key]) => {
        dispatch(
          setBulkItems({
            stage: key,
            checkedItems: [],
            uncheckedItems: [],
            selectedItems: 0,
            selectedAll: false,
          })
        )
      })

      dispatch(setModal(''))
    }
  }, [success])

  useEffect(() => {
    if (error) {
      addToast(
        error?.response?.data?.message ||
          error?.response?.message ||
          'Something went wrong',
        {
          appearance: 'error',
          autoDismiss: true,
        }
      )
    }
  }, [error])

  useEffect(() => {
    let updatedReminders = [...emailReminders]

    if (selectedStage !== 'coaching') {
      updatedReminders = emailReminders.filter(
        (reminder) => reminder.title !== 'Coaching Interview Reminder'
      )
    }

    setEmailReminders(updatedReminders)
  }, [])

  useEffect(() => {
    const handleReminderParameterChanges = () => {
      const parameterUpdates = {
        'Remote Fitness Assessment Reminder': {
          add: { remote_fitness_referral_email: referralEmail },
          remove: ['remote_fitness_referral_email'],
        },
        'Skills Test Submission Reminder': {
          add: {
            skills_test_referral_email: referralEmail,
            tests_url: [],
            test_subject: '',
          },
          remove: ['skills_test_referral_email', 'tests_url', 'test_subject'],
        },
      }

      emailReminders.forEach((reminder) => {
        const updates = parameterUpdates[reminder.title]
        if (!updates) return

        if (reminder.checked) {
          setEmailParameters((prev) => ({ ...prev, ...updates.add }))
        } else {
          setEmailParameters((prev) => {
            const newParams = { ...prev }
            updates.remove.forEach((key) => delete newParams[key])
            return newParams
          })
        }
      })
    }

    handleReminderParameterChanges()
    setFieldErrors({}) // Reset field errors on parameter change
  }, [emailReminders])

  return (
    <>
      <MainModal
        info
        onClose={handleClose}
        title="Send Email Reminders"
        buttonText="Send Email Reminders"
        loading={loading}
        onClick={handleSubmit}
      >
        <H6 weight="500">
          Selected Applicants: {getTotalSelectedItems().toString()}
        </H6>

        <GroupedCheckbox
          title="Select Reminders:"
          checkboxProps={emailReminders}
          onChange={handleGroupedCheckboxChange}
        />
        <EmailParameterForm
          emailReminders={emailReminders}
          emailParameters={emailParameters}
          onChange={handleEmailParameterChange}
          fieldErrors={fieldErrors}
        />
      </MainModal>
    </>
  )
}

export default BulkSendEmailReminders
